import { computed } from 'vue';

import { ProtocolType, ProtocolTypeURLMap } from './enum';

import { usePortalLoginStore, usePortalUserMemberStore } from '@/store/user';
import { resetAnalytics } from '@/utils/analytics';

/// 清除用户信息
export const clearAllUserInfo = () => {
  const portalLoginStore = usePortalLoginStore();
  // 还需要移除用户的会员和账户的详情信息
  const memberStore = usePortalUserMemberStore();
  portalLoginStore.removePortalLogin();
  memberStore.removeUserMember();
  resetAnalytics();
};

export const handleState = async () => {
  await usePortalLoginStore().refreshTokenIfNeeded();
  console.log('Token refreshed successfully-Token--刷新成功');
  // token刷新成功后，才进行用户更新，防止并发情况下如果token接口没回调，在拿之前的token去更新用户信息导致token异常
  // 刷新用户相关信息，异步并行进行用户数据刷新，这里不用更新用户详情的接口，因为每次在index首页的onShow方法中都会调用这个方法
  // 来更新用户信息，查询用户是否关注过公众号，所以在这里就不调用了
  usePortalUserMemberStore().updateUserMemberIfNeeded();
};

/**
 * 判断日期是否为今天
 * @param date 日期字符串或时间戳
 * @returns boolean
 */
export function isToday(date: string | number | Date): boolean {
  if (!date) return false;

  // 统一处理为 Date 对象
  let targetDate: Date;
  if (typeof date === 'string') {
    // 兼容 iOS 日期格式，将 "2025-11-10" 转为 "2025/11/10"
    targetDate = new Date(date.replace(/-/g, '/'));
  } else if (typeof date === 'number') {
    targetDate = new Date(date);
  } else {
    targetDate = date;
  }

  const today = new Date();
  return (
    targetDate.getFullYear() === today.getFullYear() &&
    targetDate.getMonth() === today.getMonth() &&
    targetDate.getDate() === today.getDate()
  );
}

export function getWxAppEnvVersion(): 'release' | 'trial' | 'develop' {
  // 你可以根据实际环境变量或域名判断
  // 例如：VITE_APP_ENV=production/test/development
  const env = import.meta.env.VITE_APP_ENV;
  if (env === 'production') {
    return 'release';
  } else if (env === 'test' || env === 'pre') {
    return 'trial';
  } else if (env === 'development' || env === 'dev') {
    return 'develop';
  }
  // 默认返回正式版
  return 'release';
}

/**
 * 获取资源服务器基础地址
 * H5 环境根据 VITE_APP_ENV 判断，微信小程序根据 envVersion 判断
 */
export function getResBaseUrl(): string {
  // 优先使用单一环境变量，便于每个环境只配置一个 URL
  const direct = import.meta.env.VITE_OSS_BASEURL as string | undefined;
  if (direct) return direct;

  // 兼容旧配置：按 envVersion 回落到三套变量
  const envVersion = getWxAppEnvVersion(); // 'release' | 'trial' | 'develop'
  if (envVersion === 'release') {
    return import.meta.env.VITE_OSS_BASEURL_RES_RELEASE as string;
  } else if (envVersion === 'trial') {
    return import.meta.env.VITE_OSS_BASEURL_RES_TRIAL as string;
  } else if (envVersion === 'develop') {
    return import.meta.env.VITE_OSS_BASEURL_RES_DEVELOP as string;
  }
  // 默认返回正式版
  return (import.meta.env.VITE_OSS_BASEURL_RES_RELEASE as string) || '';
}

export const formatExpiresDateYMD = (date: string | number | Date): string => {
  let t = date;
  if (!t) return '';
  // 兼容 iOS 日期格式
  if (typeof t === 'string') {
    // 如果是 "2025-12-28 18:21:19" 这种格式，替换为 "2025/12/28 18:21:19"
    t = t.replace(/-/g, '/');
  }
  const d = new Date(t);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
};

// 格式化会员到期日期
export const formatExpiresDate = computed(() => {
  const memberStore = usePortalUserMemberStore();
  let t = memberStore.userMember?.expiresTime;
  if (!t) return '';
  // 兼容 iOS 日期格式
  if (typeof t === 'string') {
    // 如果是 "2025-12-28 18:21:19" 这种格式，替换为 "2025/12/28 18:21:19"
    t = t.replace(/-/g, '/');
  }
  const d = new Date(t);
  return formatExpiresDateYMD(d);
});

/// 根据协议类型生成协议的URL
export const genProtocolURL = (type: ProtocolType): string => {
  const resBaseUrl = getResBaseUrl();
  switch (type) {
    case ProtocolType.USER_AGREEMENT:
      return resBaseUrl + ProtocolTypeURLMap[ProtocolType.USER_AGREEMENT];
    case ProtocolType.PRIVACY_POLICY:
      return resBaseUrl + ProtocolTypeURLMap[ProtocolType.PRIVACY_POLICY];
    case ProtocolType.WITHDRAW_PROTOCOL:
      return resBaseUrl + ProtocolTypeURLMap[ProtocolType.WITHDRAW_PROTOCOL];
    case ProtocolType.MEMBERSHIP_SERVICE_AGREEMENT:
      return resBaseUrl + ProtocolTypeURLMap[ProtocolType.MEMBERSHIP_SERVICE_AGREEMENT];
    case ProtocolType.RECOMMENDER_SERVICE_AGREEMENT:
      return resBaseUrl + ProtocolTypeURLMap[ProtocolType.RECOMMENDER_SERVICE_AGREEMENT];
    default:
      return resBaseUrl + ProtocolTypeURLMap[ProtocolType.PRIVACY_POLICY];
  }
};
/// 是否显示关注公众号的提示
export const showGZHNotification = () => {
  const portalLoginStore = usePortalLoginStore();
  if (portalLoginStore.portalLogin) {
    return !portalLoginStore.portalLogin.user.isFollow;
  }
  return true;
};

// 获取系统环境 ios/android/other
export function getSystemEnv(): 'ios' | 'android' | 'other' {
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) {
    return 'ios';
  } else if (/android/.test(ua)) {
    return 'android';
  }
  return 'other';
}

/// 根据图标路径生成完整的OSS图标URL
export const iconUrlFromOss = (iconUrl: string): string => {
  const resBaseUrl = getResBaseUrl();
  const url = `${resBaseUrl}portal/ma-wc/icon/${iconUrl}`;
  return url;
};

/** 是否为可用的文件 Blob（排除 JSON 业务错误、空包） */
export const blobValidate = (data: any) => {
  if (!(data instanceof Blob)) return false;
  if (data.size <= 0) return false;
  return data.type !== 'application/json';
};

/** 网关超时等场景可能把 JSON 错误标成 octet-stream；小体积且以 { 开头时按错误处理 */
export const looksLikeJsonErrorBlob = async (data: Blob): Promise<boolean> => {
  if (!(data instanceof Blob) || data.size <= 0) return false;
  if (data.type === 'application/json') return true;
  // 正常 xlsx 远大于此；错误 JSON 通常很小
  if (data.size > 4096) return false;
  try {
    const head = (await data.slice(0, 64).text()).trimStart();
    return head.startsWith('{') || head.startsWith('[');
  } catch {
    return false;
  }
};
